import { Reveal, SectionHeader } from "./Reveal";
import { AlbumPlayer } from "./AlbumPlayer";
import { PARA_TI_TRACKS, ESENCIA_TRACKS } from "../lib/tracks";
import { ArrowUpRight, Disc3 } from "lucide-react";

const ALBUMS = [
  {
    title: "Para ti, un homenaje",
    year: "1998",
    cover: "/photos/caratula-para-ti.png",
    spotify: "https://open.spotify.com/search/Para%20ti%20un%20homenaje%20Cuarto%20Bolero",
    tracks: PARA_TI_TRACKS,
  },
  {
    title: "Esencia de Bolero",
    year: "2003",
    cover: "/photos/caratula-esencia.png",
    spotify: "https://open.spotify.com/search/Esencia%20de%20Bolero%20Cuarto%20Bolero",
    tracks: [
      { title: "LLévatela", src: "/music/esencia-01-llevatela.mp3" },
      { title: "Orfeo negro", src: "/music/esencia-02-orfeo-negro.mp3" },
      { title: "La negra Tomasa", src: "/music/esencia-03-la-negra-tomasa.mp3" },
      { title: "Amor, no me quieras tanto", src: "/music/esencia-04-amor-no-me-quieras-tanto.mp3" },
      { title: "Vivencias", src: "/music/esencia-05-vivencias.mp3" },
      { title: "Licencia", src: "/music/esencia-06-licencia.mp3" },
      { title: "Sabor a mí", src: "/music/esencia-07-sabor-a-mi.mp3" },
      { title: "Flor de un día", src: "/music/esencia-08-flor-de-un-dia.mp3" },
      { title: "Sombras", src: "/music/esencia-09-sombras.mp3" },
      { title: "Dos gardenias", src: "/music/esencia-10-dos-gardenias.mp3" },
      { title: "Échame a mí la culpa", src: "/music/esencia-12-echame-a-mi-la-culpa.mp3" },
      { title: "Me voy pa'l pueblo", src: "/music/esencia-13-me-voypal-pueblo.mp3" },
    ],
  },
];

const REPERTOIRE = [
  { song: "Sabor a mí", composer: "Álvaro Carrillo" },
  { song: "Si tú me dices ven", composer: "Alfredo “Güero” Gil" },
  { song: "Contigo", composer: "Claudio Estrada" },
  { song: "Historia de un amor", composer: "Carlos Eleta Almarán" },
  { song: "Guantanamera", composer: "Joselito Fernández" },
  { song: "Lágrimas negras", composer: "Miguel Matamoros" },
  { song: "María Cristina", composer: "Clásico" },
  { song: "El vagabundo", composer: "Clásico" },
  { song: "Rayito de luna", composer: "Clásico" },
];

export const Music = () => (
  <section id="musica" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-24">
      <SectionHeader
        eyebrow="Música"
        title={
          <>
            Dos álbumes, un repertorio <span className="italic text-gold">inmortal</span>.
          </>
        }
      />

      <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-14">
        <div className="md:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 items-start">
            {ALBUMS.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.08}>
                <div data-testid={`album-card-${i + 1}`} className="group">
                  <div className="relative">
                    <div className="absolute inset-0 translate-x-3 translate-y-3 border border-gold/30 transition-transform duration-500 group-hover:translate-x-4 group-hover:translate-y-4" aria-hidden="true" />
                    <img src={a.cover} alt={`Portada de ${a.title}`} className="relative w-full aspect-square object-cover" />
                    <div className="absolute top-4 left-4 bg-ink/70 backdrop-blur px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] text-gold border border-gold/30">
                      Álbum · {a.year}
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-2xl text-cream">{a.title}</h3>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-mute">Cuarto Bolero · {a.year}</p>
                    </div>
                    <a
                      data-testid={`album-spotify-link-${i + 1}`}
                      href={a.spotify}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Escuchar ${a.title} en Spotify`}
                      className="shrink-0 inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
                    >
                      <Disc3 size={15} />
                      Escuchar
                    </a>
                  </div>
                </div>
                <AlbumPlayer playerId={`album-${i + 1}`} title={a.title} year={a.year} tracks={a.tracks} />
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-12 max-w-2xl text-base md:text-lg leading-relaxed text-sand">
              Sus dos álbumes se han convertido en referentes dentro de la música de trío, destacando por su calidad instrumental y su inconfundible estilo al versionar los grandes clásicos del bolero y el son cubano.
            </p>
          </Reveal>
        </div>

        <div className="md:col-span-5">
          <Reveal>
            <p className="eyebrow">Repertorio</p>
          </Reveal>
          <div className="mt-6">
            {REPERTOIRE.map((r, i) => (
              <Reveal key={r.song} delay={i * 0.03}>
                <div data-testid={`repertoire-row-${i + 1}`} className="group flex items-center justify-between gap-4 border-t border-cream/10 py-4 transition-all duration-300 hover:pl-3 hover:bg-cream/[0.02]">
                  <div className="flex items-baseline gap-4">
                    <span className="text-xs text-mute">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-serif text-xl md:text-2xl text-cream group-hover:text-gold transition-colors">{r.song}</span>
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-mute text-right">{r.composer}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-6 text-sm text-mute leading-relaxed">
              Y un sinfín de clásicos del cancionero latino, versionados con el sello inconfundible del trío.
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
